import { useSuspenseQuery } from '@apollo/client';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, TextField, Typography } from '@mui/material';
import Fuse from 'fuse.js';
import { useEffect, useState, type FC } from 'react';
import { FixedSizeList } from 'react-window-1.8.10';
import { TabContent, TabProps } from '../common';
import AllUsersAccessListItem from './AllUsersAccessListItem';
import GET_ALL_DEVICE_ACCESS_FOR_DEVICE, {
  DeviceUserAccessEntry,
} from './graphql/queries/getAllDeviceAccessForDevice';

export const AllAccessTab: FC<Omit<TabProps, 'setIsDirty'>> = ({ deviceId }) => {
  const { _ } = useLingui();
  const { data } = useSuspenseQuery(GET_ALL_DEVICE_ACCESS_FOR_DEVICE, {
    variables: { deviceId },
  });
  const [organizationIds, setOrganizationIds] = useState<number[]>([]);
  const [search, setSearch] = useState('');

  const allEntries = data.device.usersWithAccess;

  const [filteredEntries, setFilteredEntries] = useState(allEntries);

  useEffect(() => {
    // Orden: acceso directo primero, luego solo-colección; dentro de cada grupo, alfabético por nombre.
    let items = [...allEntries].sort((a, b) => {
      if (a.direct !== b.direct) return a.direct ? -1 : 1;
      return a.user.fullName.localeCompare(b.user.fullName, undefined, {
        sensitivity: 'base',
        numeric: true,
      });
    });

    if (organizationIds.length) {
      const orgIdSet = new Set(organizationIds);
      items = items.filter(
        (entry) => entry.user.mainOrganization && orgIdSet.has(entry.user.mainOrganization.organization.id),
      );
    }

    if (search) {
      const fuse = new Fuse(items, { keys: ['user.fullName', 'user.email', 'user.id'] });
      items = fuse.search(search).map(({ item }) => item);
    }

    setFilteredEntries(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, organizationIds, allEntries]);

  return (
    <TabContent>
      <Typography variant="h6" mb={2}>
        <Trans>Todos los usuarios con acceso al dispositivo</Trans>
      </Typography>
      <Box mb={1} display="flex" justifyContent="stretch" alignItems="center" gap={2}>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          label={_(msg`Buscar`)}
          sx={{ flex: 3 }}
        />
        <StandardAutocompleteIdsWithFallback
          sx={{ flex: 2 }}
          size="small"
          dataType="organizations"
          multiple
          multipleCheckboxes
          value={organizationIds}
          onChange={(_event, values) => setOrganizationIds(values)}
        />
        <Typography textAlign="right">
          <Trans>
            <b>{allEntries.length}</b> usuarios con acceso
          </Trans>
        </Typography>
      </Box>
      <Box border={1} borderColor="divider" borderRadius={1}>
        <FixedSizeList
          height={380}
          width="100%"
          itemSize={56}
          itemCount={filteredEntries.length}
          itemKey={(index: number) => filteredEntries[index].user.id}
        >
          {({ style, index }: { style: React.CSSProperties; index: number }) => {
            const entry: DeviceUserAccessEntry = filteredEntries[index];
            return (
              <AllUsersAccessListItem
                key={entry.user.id}
                style={style}
                lastItem={index === filteredEntries.length - 1}
                userId={entry.user.id}
                userName={entry.user.fullName}
                userEmail={entry.user.email}
                userOrganization={
                  entry.user.mainOrganization
                    ? {
                        id: entry.user.mainOrganization.organization.id,
                        name: entry.user.mainOrganization.organization.name,
                        roleId: entry.user.mainOrganization.role.id,
                      }
                    : null
                }
                direct={entry.direct}
                collections={entry.collections}
                visibleToUser={entry.visibleToUser}
                operationalState={entry.device.profile?.operationalState}
                deviceVisibleToClient={entry.device.visibleToClient}
                operationalStateVisibleToClient={entry.device.operationalStateVisibleToClient}
              />
            );
          }}
        </FixedSizeList>
      </Box>
    </TabContent>
  );
};
